import { Capacitor } from '@capacitor/core';
import { API_BASE_URL } from '../api';
import { LOCAL_WEB_VERSION } from '../buildInfo';
import { storageGet, storageSet } from './safeStorage';

const DISMISS_KEY = 'nmhelper_update_dismissed_version';
const PLAY_STORE_HTTPS = 'https://play.google.com/store/apps/details?id=in.nmhelper.app';
const PLAY_STORE_MARKET = 'market://details?id=in.nmhelper.app';

const VERSION_SOURCES = [
  () => `${API_BASE_URL}/app-version`,
  () => process.env.REACT_APP_VERSION_JSON_URL || 'https://app.nmhelper.in/app-version.json',
  () => '/app-version.json',
];

export async function fetchRemoteAppVersion() {
  const errors = [];
  for (const getUrl of VERSION_SOURCES) {
    const base = getUrl();
    if (!base) continue;
    try {
      const sep = base.includes('?') ? '&' : '?';
      const res = await fetch(`${base}${sep}t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) {
        errors.push(`${base}: ${res.status}`);
        continue;
      }
      return await res.json();
    } catch (err) {
      errors.push(`${base}: ${err.message}`);
    }
  }
  throw new Error(errors.join('; ') || 'version fetch failed');
}

function compareSemver(a, b) {
  const pa = String(a || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    const da = pa[i] || 0;
    const db = pb[i] || 0;
    if (da > db) return 1;
    if (da < db) return -1;
  }
  return 0;
}

export function isDismissedVersion(token) {
  return storageGet(DISMISS_KEY) === String(token);
}

export function dismissUpdateVersion(token) {
  storageSet(DISMISS_KEY, String(token));
}

async function getNativeBuildInfo() {
  if (!Capacitor.isNativePlatform()) {
    return { version: '', build: 0 };
  }
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return {
      version: info?.version || '',
      build: parseInt(info?.build, 10) || 0,
    };
  } catch {
    return { version: '', build: 0 };
  }
}

function buildAndroidDismissToken(remote, latestCode) {
  const version = remote.android || remote.web || '';
  return `android:${version}:${latestCode}`;
}

function buildWebDismissToken(remoteWeb) {
  return `web:${remoteWeb}`;
}

/** Unregister SW, clear caches, hard reload — for stale iOS Safari / PWA shells. */
export async function hardRefreshWebApp() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.unregister()));
    }
  } catch {
    // ignore
  }
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // ignore
  }
  const url = new URL(window.location.href);
  url.searchParams.set('_nm_refresh', String(Date.now()));
  window.location.replace(url.toString());
}

/**
 * @returns {Promise<{
 *   kind: 'android'|'web'|null,
 *   remoteVersion: string,
 *   localVersion?: string,
 *   storeUrl: string,
 *   marketUrl: string,
 *   notes?: string,
 *   force?: boolean,
 *   dismissToken?: string,
 *   nativeVersion?: string,
 *   nativeBuild?: number,
 * }>}
 */
export async function checkForAppUpdate() {
  const isAndroidNative =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

  if (isAndroidNative) {
    const remote = await fetchRemoteAppVersion();
    const storeUrl = remote.playStoreUrl || PLAY_STORE_HTTPS;
    const marketUrl = remote.marketUrl || PLAY_STORE_MARKET;
    const targetVersion = remote.android || remote.web || '';
    const latestCode = parseInt(remote.androidLatestVersionCode, 10) || 0;
    const minCode = parseInt(remote.androidMinVersionCode, 10) || 0;

    const { version: nativeVersion, build: nativeBuild } = await getNativeBuildInfo();

    const behindByCode = latestCode > 0 && nativeBuild > 0 && nativeBuild < latestCode;
    const behindByMin = minCode > 0 && nativeBuild > 0 && nativeBuild < minCode;
    const behindBySemver =
      nativeVersion && targetVersion && compareSemver(targetVersion, nativeVersion) > 0;

    if (!behindByCode && !behindByMin && !behindBySemver) {
      return { kind: null, remoteVersion: targetVersion, storeUrl, marketUrl };
    }

    const dismissToken = buildAndroidDismissToken(remote, latestCode || minCode);
    const force = Boolean(remote.forceUpdate) || behindByMin;

    if (!force && isDismissedVersion(dismissToken)) {
      return { kind: null, remoteVersion: targetVersion, storeUrl, marketUrl };
    }

    return {
      kind: 'android',
      remoteVersion: targetVersion,
      storeUrl,
      marketUrl,
      notes: remote.releaseNotes,
      force,
      dismissToken,
      nativeVersion,
      nativeBuild,
    };
  }

  // Website / iOS Safari / Chrome / installed PWA
  if (Capacitor.isNativePlatform()) {
    return { kind: null, remoteVersion: '', storeUrl: PLAY_STORE_HTTPS, marketUrl: PLAY_STORE_MARKET };
  }

  const remote = await fetchRemoteAppVersion();
  const remoteWeb = String(remote.web || '').trim();
  const localWeb = String(LOCAL_WEB_VERSION || '').trim();
  const storeUrl = remote.playStoreUrl || PLAY_STORE_HTTPS;
  const marketUrl = remote.marketUrl || PLAY_STORE_MARKET;

  if (!remoteWeb || !localWeb || compareSemver(remoteWeb, localWeb) <= 0) {
    return {
      kind: null,
      remoteVersion: remoteWeb,
      localVersion: localWeb,
      storeUrl,
      marketUrl,
    };
  }

  const dismissToken = buildWebDismissToken(remoteWeb);
  if (isDismissedVersion(dismissToken)) {
    return {
      kind: null,
      remoteVersion: remoteWeb,
      localVersion: localWeb,
      storeUrl,
      marketUrl,
    };
  }

  return {
    kind: 'web',
    remoteVersion: remoteWeb,
    localVersion: localWeb,
    storeUrl,
    marketUrl,
    notes: remote.releaseNotes,
    force: false,
    dismissToken,
  };
}
